import React from 'react';
import { Tooltip } from 'antd';
import { ChangeDisplay } from '../styles';
import { PnlCell, explainReferenceReason } from '../../../utils/pairPnlDisplay';
import { formatNumber, formatDecimal, formatCompact } from '../../../utils/numberFormat';

/**
 * One per-leg PnL cell, in thousand VND, labelled by where the number came from.
 *
 * The three sources answer different questions and must not look alike:
 *   - a filled leg shows REAL unrealized money, so it renders bare;
 *   - a flat leg shows what ONE pair would make if it opened now at the
 *     reference prices, which is a hypothetical and carries a `/ PAIR` suffix.
 * Without the suffix the reference number reads as a position PnL, and an
 * operator would think they were holding something they are not.
 *
 * `engine` vs `preview` is the same arithmetic from two places: the engine's own
 * published breakdown (authoritative — it IS the trigger) versus a client-side
 * preview of the form's values. That distinction lives in the tooltip and in the
 * banner's source line, NOT in the suffix: it applies to the whole panel at once,
 * so encoding it per cell was noise on every row.
 */
const REF_BADGE_COLOR = '#848E9C';

export interface PnlCellDisplayProps {
    cell: PnlCell | null;
    /** Unit label for the quantity in the tooltip ("shares" / "contract(s)"). */
    quantityUnit: string;
    /** Decimals for the average-entry price in the tooltip. */
    costPrecision?: number;
    /** Divisor to turn `averageCost` into its display unit (stock: 1000). */
    costDivisor?: number;
    /** Engine's reason the trigger is undefined, for the flat-leg tooltip. */
    undefinedReason?: string | null;
    /** The column header already says "/ pair" (every row is flat). */
    hidePerPairBadge?: boolean;
}

const PnlCellDisplay: React.FC<PnlCellDisplayProps> = ({
    cell,
    quantityUnit,
    costPrecision = 2,
    costDivisor = 1,
    undefinedReason,
    hidePerPairBadge,
}) => {
    if (!cell) {
        return <span style={{ color: REF_BADGE_COLOR }}>—</span>;
    }

    if (cell.pnl === null) {
        // The source has the leg but cannot value it — say why rather than
        // showing a 0 that looks like "flat and fine".
        const why = explainReferenceReason(cell.note ?? undefinedReason);
        return (
            <Tooltip title={why ? `No trigger value: ${why}` : 'No trigger value yet'}>
                <span style={{ color: REF_BADGE_COLOR }}>n/a</span>
            </Tooltip>
        );
    }

    const thousand = cell.pnl / 1000;
    const value = (
        <ChangeDisplay positive={thousand >= 0}>
            {thousand >= 0 ? '+' : ''}
            {formatCompact(thousand, 1)}
        </ChangeDisplay>
    );

    if (cell.source === 'position') {
        const avg = (cell.averageCost ?? 0) / costDivisor;
        return (
            <Tooltip
                title={
                    `Open position — real unrealized PnL. ` +
                    `${cell.side} ${formatNumber(cell.quantity ?? 0)} ${quantityUnit} ` +
                    `@ avg ${formatDecimal(avg, costPrecision)}` +
                    (cell.stale ? ' — no live tick yet, backend mark' : '')
                }
            >
                {value}
            </Tooltip>
        );
    }

    const isEngine = cell.source === 'engine';
    const note = explainReferenceReason(cell.note);
    return (
        <Tooltip
            title={
                `${isEngine ? "Engine's OPEN trigger" : 'Preview from this form'} — ` +
                `PnL of ONE pair opened now at the reference prices. Not a position. ` +
                `Size: ${formatNumber(cell.quantity ?? 0)} ${quantityUnit}.` +
                (isEngine
                    ? ''
                    : ' Computed in the browser from the values on screen; once the bot runs on them, its own number replaces this.') +
                (note ? ` (${note})` : '')
            }
        >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                {value}
                {!hidePerPairBadge && <span
                    style={{
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        color: REF_BADGE_COLOR,
                    }}
                >
                    / PAIR
                </span>}
            </span>
        </Tooltip>
    );
};

export default PnlCellDisplay;
